/** Default dial country for the call center (Oman). */
export const DEFAULT_COUNTRY_CODE = "968";
export const DEFAULT_COUNTRY_ISO = "om";
export const LOCAL_NUMBER_MAX_LENGTH = 8;
export const LOCAL_NUMBER_MIN_LENGTH = 7;
export const MAX_MOBILE_NUMBERS = 5;

/** GCC + common expat countries shown first in the country picker. */
export const PREFERRED_COUNTRY_ISOS = ["om", "ae", "sa", "kw", "bh", "qa", "in", "pk", "eg", "gb"];

export const PHONE_VALIDATION = {
  EMPTY: "EMPTY",
  INVALID: "INVALID",
  INVALID_OMAN: "INVALID_OMAN",
  DUPLICATE: "DUPLICATE",
};

export const normalizeDigits = (value) => String(value ?? "").replace(/\D/g, "");

export const isBlankPhoneValue = (phone, dialCode = DEFAULT_COUNTRY_CODE) => {
  const digits = normalizeDigits(phone);
  if (!digits) return true;
  const normalizedDial = normalizeDigits(dialCode);
  if (!normalizedDial) return false;
  if (digits === normalizedDial) return true;
  return digits.length <= normalizedDial.length;
};

/**
 * Ensures stored phone values include the country code.
 * Local-length numbers default to Oman (+968).
 */
export const ensureE164Digits = (
  value,
  defaultCountryCode = DEFAULT_COUNTRY_CODE,
  localMaxLength = LOCAL_NUMBER_MAX_LENGTH
) => {
  const digits = normalizeDigits(value);
  if (!digits) return "";
  if (digits.startsWith(defaultCountryCode)) return digits;
  if (localMaxLength > 0 && digits.length <= localMaxLength) {
    return `${defaultCountryCode}${digits}`;
  }
  return digits;
};

export const sanitizePhoneValue = (value) => {
  if (isBlankPhoneValue(value)) return "";
  return ensureE164Digits(value);
};

export const toPhoneInputValue = (storedValue) => sanitizePhoneValue(storedValue);

export const parseMobileNumberList = (value) => {
  if (!value) return [""];
  const parts = String(value)
    .split(/[,;]+/)
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => sanitizePhoneValue(item))
    .filter(Boolean);
  return parts.length ? parts : [""];
};

export const joinMobileNumberList = (mobileNumbers) =>
  (mobileNumbers || [])
    .map((value) => sanitizePhoneValue(value))
    .filter(Boolean)
    .filter((value, index, all) => all.indexOf(value) === index)
    .join(", ");

export const formatPhoneDisplay = (value) => {
  const digits = sanitizePhoneValue(value);
  if (!digits) return "";
  if (digits.startsWith(DEFAULT_COUNTRY_CODE) && digits.length > DEFAULT_COUNTRY_CODE.length) {
    const local = digits.slice(DEFAULT_COUNTRY_CODE.length);
    return `+${DEFAULT_COUNTRY_CODE} ${local}`;
  }
  if (digits.length > 3) {
    return `+${digits.slice(0, 3)} ${digits.slice(3)}`;
  }
  return `+${digits}`;
};

export const validatePhoneNumber = (value) => {
  const digits = sanitizePhoneValue(value);
  if (!digits) return PHONE_VALIDATION.EMPTY;

  if (digits.startsWith(DEFAULT_COUNTRY_CODE)) {
    const local = digits.slice(DEFAULT_COUNTRY_CODE.length);
    if (local.length < LOCAL_NUMBER_MIN_LENGTH || local.length > LOCAL_NUMBER_MAX_LENGTH) {
      return PHONE_VALIDATION.INVALID_OMAN;
    }
    return null;
  }

  if (digits.length < 10 || digits.length > 15) {
    return PHONE_VALIDATION.INVALID;
  }
  return null;
};

export const findDuplicatePhoneIndexes = (values = []) => {
  const seen = new Map();
  const duplicates = new Set();
  values.forEach((value, index) => {
    const digits = sanitizePhoneValue(value);
    if (!digits) return;
    if (seen.has(digits)) {
      duplicates.add(index);
      duplicates.add(seen.get(digits));
    } else {
      seen.set(digits, index);
    }
  });
  return duplicates;
};

export const hasValidMobileNumbers = (values = []) =>
  values.some((value) => validatePhoneNumber(value) === null);

export const mapPhoneValidationMessage = (code, t) => {
  switch (code) {
    case PHONE_VALIDATION.INVALID_OMAN:
      return t("ccMobileNumberInvalidOman");
    case PHONE_VALIDATION.INVALID:
      return t("ccMobileNumberInvalid");
    case PHONE_VALIDATION.DUPLICATE:
      return t("ccMobileNumberDuplicate");
    default:
      return null;
  }
};
